/**
 * useFileAttachment - Hook para gestão de arquivos anexados
 * Gerencia upload, preview, validação e remoção de arquivos
 */

import { useState, useCallback } from 'react';
import type { DragAndPasteFile } from './useDragAndPaste';

export interface AttachmentFile extends DragAndPasteFile {
  /** Status do upload */
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'failed';
  /** Progresso do upload (0-100) */
  uploadProgress: number;
  /** URL do arquivo após upload */
  url?: string;
  /** Erro de upload */
  error?: string;
}

interface UseFileAttachmentOptions {
  /** Callback quando upload é concluído */
  onUploadComplete?: (file: AttachmentFile) => void;
  /** Callback quando upload falha */
  onUploadError?: (file: AttachmentFile, error: string) => void;
  /** Função customizada de upload - pode retornar URL string OU objeto completo da mensagem */
  uploadFn?: (file: File, onProgress: (progress: number) => void, caption?: string, replyToMessageId?: string) => Promise<string | any>;
}

/**
 * Hook para gerenciar anexos de arquivos
 *
 * @example
 * ```tsx
 * const { attachments, addAttachments, removeAttachment, uploadAttachment } = useFileAttachment({
 *   uploadFn: async (file, onProgress) => {
 *     // Upload implementation
 *     return 'https://cdn.example.com/file.jpg';
 *   },
 * });
 * ```
 */
export const useFileAttachment = (options: UseFileAttachmentOptions = {}) => {
  const { onUploadComplete, onUploadError, uploadFn } = options;

  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);

  /**
   * Adiciona arquivos à lista de anexos
   */
  const addAttachments = useCallback((files: DragAndPasteFile[]) => {
    const newAttachments: AttachmentFile[] = files.map((file) => ({
      ...file,
      uploadStatus: 'pending',
      uploadProgress: 0,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  /**
   * Remove anexo por ID
   */
  const removeAttachment = useCallback((fileId: string) => {
    setAttachments((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  /**
   * Limpa todos os anexos
   */
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  /**
   * Restaura anexos de um rascunho por conversa (chatDraftStore).
   * Substitui a lista inteira — usado na troca de conversa do ChatBox.
   */
  const restoreAttachments = useCallback((files: AttachmentFile[]) => {
    setAttachments(files);
  }, []);

  /**
   * Atualiza status de upload de um anexo
   */
  const updateAttachmentStatus = useCallback(
    (
      fileId: string,
      updates: Partial<Pick<AttachmentFile, 'uploadStatus' | 'uploadProgress' | 'url' | 'error'>>
    ) => {
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.id === fileId ? { ...attachment, ...updates } : attachment
        )
      );
    },
    []
  );

  /**
   * Faz upload de um anexo específico
   * @returns A URL do arquivo após upload OU a resposta completa do servidor (se uploadFn retornar objeto)
   */
  const uploadAttachment = useCallback(
    async (fileId: string, caption?: string, replyToMessageId?: string): Promise<string | any> => {
      const attachment = attachments.find((a) => a.id === fileId);
      if (!attachment) return '';

      // Verificar se já está em upload
      if (attachment.uploadStatus === 'uploading' || attachment.uploadStatus === 'completed') {
        return '';
      }

      // Marcar como uploading
      updateAttachmentStatus(fileId, {
        uploadStatus: 'uploading',
        uploadProgress: 0,
        error: undefined,
      });

      try {
        if (!uploadFn) {
          throw new Error('Upload function not provided');
        }

        // Callback de progresso
        const onProgress = (progress: number) => {
          updateAttachmentStatus(fileId, { uploadProgress: progress });
        };

        // Executar upload - pode retornar URL string OU mensagem completa (objeto)
        const result = await uploadFn(attachment.file, onProgress, caption, replyToMessageId);

        // ✅ CORREÇÃO: Extrair URL se result for objeto (ConversationMessageResponse)
        let url: string;
        if (typeof result === 'object' && result.content) {
          // Result é ConversationMessageResponse - extrair URL do content
          const mediaContent = result.content.find(
            (c: any) => c.type === 'image' || c.type === 'video' || c.type === 'audio' || c.type === 'document'
          );
          url = (mediaContent as any)?.url || '';
        } else {
          // Result é string (URL direta)
          url = result as string;
        }

        // Marcar como completo
        updateAttachmentStatus(fileId, {
          uploadStatus: 'completed',
          uploadProgress: 100,
          url,
        });

        // Callback de sucesso
        const updatedAttachment = attachments.find((a) => a.id === fileId);
        if (updatedAttachment) {
          onUploadComplete?.({ ...updatedAttachment, uploadStatus: 'completed', url });
        }

        // ✅ Retornar resultado completo (URL ou resposta do servidor)
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';

        // Marcar como falha
        updateAttachmentStatus(fileId, {
          uploadStatus: 'failed',
          error: errorMessage,
        });

        // Callback de erro
        const updatedAttachment = attachments.find((a) => a.id === fileId);
        if (updatedAttachment) {
          onUploadError?.(
            { ...updatedAttachment, uploadStatus: 'failed', error: errorMessage },
            errorMessage
          );
        }

        throw error;  // ✅ Re-throw para que caller possa tratar
      }
    },
    [attachments, uploadFn, updateAttachmentStatus, onUploadComplete, onUploadError]
  );

  /**
   * Faz upload de todos os anexos pendentes
   */
  const uploadAllAttachments = useCallback(async () => {
    const pendingAttachments = attachments.filter(
      (a) => a.uploadStatus === 'pending' || a.uploadStatus === 'failed'
    );

    await Promise.all(pendingAttachments.map((a) => uploadAttachment(a.id)));
  }, [attachments, uploadAttachment]);

  /**
   * Retenta upload de anexo falhado
   */
  const retryUpload = useCallback(
    (fileId: string) => {
      updateAttachmentStatus(fileId, {
        uploadStatus: 'pending',
        uploadProgress: 0,
        error: undefined,
      });

      uploadAttachment(fileId);
    },
    [updateAttachmentStatus, uploadAttachment]
  );

  /**
   * Verifica se todos os anexos foram enviados
   */
  const allUploadsComplete = attachments.length > 0 && attachments.every(
    (a) => a.uploadStatus === 'completed'
  );

  /**
   * Verifica se algum upload está em progresso
   */
  const isUploading = attachments.some((a) => a.uploadStatus === 'uploading');

  /**
   * Verifica se há uploads com falha
   */
  const hasFailedUploads = attachments.some((a) => a.uploadStatus === 'failed');

  /**
   * Obtém estatísticas de upload
   */
  const getUploadStats = useCallback(() => {
    return {
      total: attachments.length,
      pending: attachments.filter((a) => a.uploadStatus === 'pending').length,
      uploading: attachments.filter((a) => a.uploadStatus === 'uploading').length,
      completed: attachments.filter((a) => a.uploadStatus === 'completed').length,
      failed: attachments.filter((a) => a.uploadStatus === 'failed').length,
    };
  }, [attachments]);

  /**
   * Obtém URLs de todos os arquivos com upload completo
   */
  const getCompletedUrls = useCallback(() => {
    return attachments
      .filter((a) => a.uploadStatus === 'completed' && a.url)
      .map((a) => a.url as string);
  }, [attachments]);

  /**
   * Obtém tipo de mídia do arquivo
   */
  const getFileType = useCallback((file: AttachmentFile): 'image' | 'video' | 'audio' | 'document' => {
    if (file.file.type.startsWith('image/')) return 'image';
    if (file.file.type.startsWith('video/')) return 'video';
    if (file.file.type.startsWith('audio/')) return 'audio';
    return 'document';
  }, []);

  /**
   * Formata tamanho do arquivo
   */
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  return {
    /** Lista de anexos */
    attachments,
    /** Adiciona novos anexos */
    addAttachments,
    /** Remove anexo por ID */
    removeAttachment,
    /** Limpa todos os anexos */
    clearAttachments,
    /** Restaura anexos de um rascunho por conversa */
    restoreAttachments,
    /** Faz upload de anexo específico */
    uploadAttachment,
    /** Faz upload de todos os anexos pendentes */
    uploadAllAttachments,
    /** Retenta upload de anexo falhado */
    retryUpload,
    /** Se todos os uploads foram concluídos */
    allUploadsComplete,
    /** Se algum upload está em progresso */
    isUploading,
    /** Se há uploads com falha */
    hasFailedUploads,
    /** Estatísticas de upload */
    getUploadStats,
    /** URLs de arquivos com upload completo */
    getCompletedUrls,
    /** Obtém tipo de mídia do arquivo */
    getFileType,
    /** Formata tamanho do arquivo */
    formatFileSize,
  };
};
